import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { TerminalClause } from '@atlaskit/jql-ast';
import type {
	SearchTemplate,
	SelectOption,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type { useHydrationQueryLoaderQuery$data } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import { OperandValues } from '../../../../../common/constants.tsx';
import { useHydratedValues } from '../use-hydrated-values/useHydratedValues.tsx';

const isEmptyOption = (option: SelectOption) =>
	option.optionType === 'option' && option.value === OperandValues.empty.value;

export const useSelectedValues = (
	terminalClause: TerminalClause | undefined,
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data,
	fieldId: string,
	operandDisplayValues: string[] | undefined,
	searchTemplate: SearchTemplate,
) => {
	// Allow optimistic updates when a value is selected while hydration is taking place
	const [optimisticValues, setOptimisticValues] = useState<SelectValue>([]);
	const [hydrated, setHydrated] = useState(
		fg('render_jql_builder_in_ssr') ? !!hydratedData : false,
	);
	const prevHydratedData = useRef<
		useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data | undefined
	>(fg('render_jql_builder_in_ssr') ? hydratedData : undefined);

	const hydratedValues = useHydratedValues(
		fieldId,
		hydratedData,
		searchTemplate,
		terminalClause,
		operandDisplayValues,
	);

	// Only flag values as hydrated after a data change
	useEffect(() => {
		if (!hydrated && hydratedData !== prevHydratedData.current) {
			setHydrated(true);
		}
		prevHydratedData.current = hydratedData;
	}, [hydrated, hydratedData]);

	const selectedValues = useMemo(() => {
		const values = hydrated ? hydratedValues : optimisticValues;
		const emptyIndex = values.findIndex(isEmptyOption);
		if (emptyIndex !== -1) {
			const orderedValues = [...values];
			// Rearrange selected values to keep EMPTY at the end
			orderedValues.push(orderedValues.splice(emptyIndex, 1)[0]);
			return orderedValues;
		}
		return values;
	}, [hydrated, hydratedValues, optimisticValues]);

	const setSelectedValues = useCallback((values: SelectValue) => {
		setHydrated(false);
		setOptimisticValues(values);
	}, []);

	return [selectedValues, setSelectedValues] as const;
};
